/**
 * 做听写（播放器）—— `/students/:id/dictation/:sessionId`。
 *
 * ## 手机优先
 *
 * 真实场景是：家长把手机放在桌上，孩子在纸上写。所以这一页：
 *   · 主按钮大到能用拇指按；**不用横向滚动**；
 *   · 一眼能看清「第几题 / 共几题」「正在读第几遍」「还要等几秒」；
 *   · 孩子在写的时候**不需要**碰手机 —— 播放是自动按节奏走的，只有
 *     "没听清/跳过"才需要按一下。
 *
 * ## ⚠️ 必须有「开始播放」按钮（浏览器自动播放限制）
 *
 * `speechSynthesis.speak()` 与 `<audio>.play()` 都要求**已经发生过用户手势**。
 * 页面加载就出声会被浏览器拦掉（iOS Safari 上是**完全没声音、而且不报错**）。
 * 所以这里：进入页面**不自动播**，正中间摆一个大按钮，用户点了才 `start()`。
 * 引擎里那个 `gesture` 标志就是这件事（见 `lib/dictationPlayer.ts` 文件头）。
 *
 * ## 朗读来源：家长录音优先
 *
 * 每道题上标着「家长录音」或「浏览器语音」。前者会放家长自己录的那一段
 * （这是多音字唯一可靠的读法），后者用 `speechSynthesis` 读**词本身**
 * （读不了拼音 —— 所以多音字必须靠录音覆盖）。
 *
 * ## 三态
 *
 * `questions === null` = **题目没拿到**（不是"这次听写没有题目"）；
 * 空数组才是"真的一个词都没有"（那种会话后端根本不会让它建出来）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/Bits';
import { useToast } from '../../components/ui/Toast';
import {
  IconAlertTriangle,
  IconEye,
  IconMic,
  IconPause,
  IconPlay,
  IconReplay,
  IconSkipForward,
  IconVolume2,
} from '../../components/ui/Icons';
import { toErrorMessage } from '../../lib/api/error';
import { countLabel } from '../../lib/api/dictation';
import * as sessionApi from '../../lib/api/dictationSessions';
import { useStudent } from '../../lib/queries';
import { formatClock } from '../../lib/dictationPlayer';
import { toPlayerQuestions, useDictationPlayer } from '../../lib/useDictationPlayer';
import { describeSessionOrigin } from '../../lib/dictationBuild';

export function ParentDictationSessionPage() {
  const { id = '', sessionId = '' } = useParams<{ id: string; sessionId: string }>();
  const toast = useToast();
  const qc = useQueryClient();

  const studentQuery = useStudent(id);
  const canEdit =
    studentQuery.data?.permission === 'owner' || studentQuery.data?.permission === 'editor';

  const sessionQuery = useQuery({
    queryKey: ['dictation-sessions', id, 'detail', sessionId] as const,
    queryFn: () => sessionApi.getSession(id, sessionId),
    enabled: id !== '' && sessionId !== '',
  });

  const session = sessionQuery.data ?? null;
  const questions = session?.questions ?? null;

  /**
   * ⚠️ 必须记忆化：每次渲染都造一个新数组会让播放引擎被重建、播放位置丢失
   * （见 `useDictationPlayer` 的依赖说明）。
   */
  const playerQuestions = useMemo(
    () => toPlayerQuestions(questions ?? []),
    [questions],
  );

  const effectiveTempo = useMemo(
    () => session?.tempo ?? { repeatCount: 2, gapSeconds: 5, betweenWordsSeconds: 12 },
    [session],
  );

  /** 进度上报：一节课只在最后发一次（批量），见下面的 pending 队列。 */
  const [pending, setPending] = useState<
    { questionId: string; status: 'played' | 'skipped' }[]
  >([]);
  const markProgress = useCallback(
    (questionId: string, status: 'played' | 'skipped') => {
      setPending((prev) => [...prev, { questionId, status }]);
    },
    [],
  );

  const progressMutation = useMutation({
    mutationFn: (changes: { questionId: string; status: 'played' | 'skipped' }[]) =>
      sessionApi.saveProgress(id, sessionId, changes),
    onSuccess: () => setPending([]),
    /**
     * 进度上报失败**不弹错误**：它不是家长要完成的事（回看时那一列只是"读过没有"），
     * 一节课失败一次就弹一个红条会让人以为听写出问题了。但也不能静默丢掉 ——
     * 所以队列**保留**，整轮结束时再试一次（见下面的结束回调）。
     */
    onError: () => undefined,
  });

  /**
   * 整轮结束：把会话标成已完成 + 补交进度。
   *
   * ## 2026-10-29 两处收紧（用户问"再听一遍/点开始听写会影响记录吗"时查出来的）
   *
   * 1. **已经 `graded` 的不许被改回 `completed`**：这一页读完会自动
   *    `updateSession({ status: 'completed' })`，而对一个已批改的会话来说，
   *    那是一次**状态倒退**（界面从「已批改」变回「已完成 · 去批改」）。
   *    判定留在数据库里，但状态不该被一次"重播"改写。
   * 2. **批量任务的批不由家长"读完即提交"**：`assignment_id` 非空的会话，
   *    进度靠孩子点「提交这一批」推进（见迁移 0020 的口径）。家长在这一页
   *    重播一遍不该把这一批算成"听过了"——他只该改播放进度（`played` 标记）。
   */
  const [finishError, setFinishError] = useState<string | null>(null);
  const finishMutation = useMutation({
    mutationFn: async () => {
      if (pending.length > 0) {
        try {
          await sessionApi.saveProgress(id, sessionId, pending);
        } catch {
          /* 进度不是关键路径：失败不阻断"标记完成" */
        }
      }
      return sessionApi.updateSession(id, sessionId, { status: 'completed' });
    },
    onSuccess: async () => {
      setPending([]);
      setFinishError(null);
      await qc.invalidateQueries({ queryKey: ['dictation-sessions', id] });
    },
    onError: (err: unknown) => setFinishError(toErrorMessage(err)),
  });

  const player = useDictationPlayer({
    questions: playerQuestions,
    tempo: effectiveTempo,
    loadAudio: useCallback(
      async (wordId: string) => {
        const audio = await sessionApi.getAudio(id, wordId);
        return audio?.dataUrl ?? null;
      },
      [id],
    ),
    markProgress,
    onFinish: useCallback(() => {
      const settled = session !== null && (session.status === 'completed' || session.status === 'graded');
      const isBatch = session !== null && session.assignmentId !== null;
      if (settled || isBatch) {
        /**
         * 两条路都只**补交播放进度**（"读过/跳过"标记），不改会话状态。
         * 批量任务的批等孩子自己点「提交这一批」；已批改的保持「已批改」。
         */
        if (pending.length > 0) {
          void sessionApi.saveProgress(id, sessionId, pending).catch(() => undefined).finally(() => setPending([]));
        }
        return;
      }
      finishMutation.mutate();
    }, [finishMutation, id, pending, session, sessionId]),
  });

  /** 开始播放时顺手把会话标成"进行中"（只标一次；失败不打扰家长）。 */
  const [markedStarted, setMarkedStarted] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const startMutation = useMutation({
    mutationFn: () => sessionApi.updateSession(id, sessionId, { status: 'in_progress' }),
    onSuccess: () => setMarkedStarted(true),
    onError: (err: unknown) => setStartError(toErrorMessage(err)),
  });

  /**
   * 等待时的倒计时（`还剩 5.2 秒`）。
   *
   * ⚠️ 为什么需要一个本地的心跳：播放引擎只在**状态迁移**时通知订阅者
   *    （每一格一次），所以 `snapshot.targetAt` 在等待期间是不变的 ——
   *    直接拿它算 `targetAt - Date.now()` 会得到一个**不动**的数字，
   *    家长看到的就是"卡在还剩 5 秒"。这里在自己的渲染层每 250ms 重算一次，
   *    只在等待时跑（不是在放音时）。
   */
  const [nowTick, setNowTick] = useState(() => Date.now());
  const waiting = player.snapshot.waiting;
  useEffect(() => {
    if (!waiting) return;
    setNowTick(Date.now());
    const timer = setInterval(() => setNowTick(Date.now()), 250);
    return () => clearInterval(timer);
  }, [waiting]);

  const onStartPressed = () => {
    player.start();
    if (!markedStarted && session !== null && session.status === 'pending' && canEdit) {
      startMutation.mutate();
    }
  };

  useEffect(() => {
    if (startError !== null) toast.showToast(`没能标记成"进行中"：${startError}`, 'error');
  }, [startError, toast]);

  // ---------------------------------------------------------------- 渲染
  if (sessionQuery.isLoading) {
    return (
      <AppShell kind="parent" title="做听写">
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (sessionQuery.isError || session === null) {
    return (
      <AppShell kind="parent" title="做听写">
        <ErrorState
          message={`没拿到这次听写：${toErrorMessage(sessionQuery.error)}`}
          onRetry={() => void sessionQuery.refetch()}
        />
      </AppShell>
    );
  }

  const snapshot = player.snapshot;
  const total = questions?.length ?? null;
  const currentQuestion =
    snapshot.position === null
      ? null
      : ((questions ?? []).find((item) => item.position === snapshot.position) ?? null);

  /** 距离下一次出声还有多久（等待时显示倒计时，孩子知道还要等）。 */
  const remainingSeconds =
    snapshot.targetAt === null ? null : Math.max(0, (snapshot.targetAt - nowTick) / 1000);

  return (
    <AppShell kind="parent" title="做听写">
      <div className="space-y-4">
        {/* ---------- 标题与来源 ---------- */}
        <Card>
          {/*
            ⚠️ 标题块给了 `min-w-[9rem]`：手机窄屏下右侧「状态 / 共 N 个词 / 先预习一下」
            是 `shrink-0`，如果标题是唯一可压缩的一半，它会被压到看不见
            （批改页与历史组头都出过这个 bug，见 2026-10-29 那两次修复）。
            有了最小宽度，放不下时右侧整组会换行（外层是 `flex-wrap`）。
          */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-[9rem] flex-1">
              <h2 className="break-words text-lg font-bold text-slate-900">{session.title}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {describeSessionOrigin(session.pickCriteria, effectiveTempo)}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              <Badge tone={session.status === 'completed' ? 'success' : 'brand'}>
                {sessionApi.sessionStatusLabel(session.status)}
              </Badge>
              <Badge tone={total === null ? 'warn' : 'neutral'}>
                共 {countLabel(total)} 个词
              </Badge>
              {/*
                预习卡入口（**开始播放之前**用）：把这一轮的词按田字格 + 拼音列出来，
                点某个字还能看笔顺动画。用户的原话是"孩子在听写之前，可以先预习看一下
                那个字怎么写，预习之后再开始听写"。

                ⚠️ 它只是一条**相关页面**的链接（不是返回入口）：返回入口仍然只有
                   左上角那个「←」，且由 `backNav.ts` 按路由推导 —— 两者不是一回事。
              */}
              <Link to={`/students/${id}/dictation/${sessionId}/preview`}>
                <Button size="sm" variant="secondary" icon={<IconEye size={16} />}>
                  先预习一下
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        {/* ---------- 题目列表拿不到 / 真的没有题目 ---------- */}
        {questions === null ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            没拿到这次的题目（后端返回的字段名与预期不一致）—— 这不是"这次听写没有题目"。
            <Button size="sm" variant="secondary" className="ml-2" onClick={() => void sessionQuery.refetch()}>
              重新取一次
            </Button>
          </p>
        ) : questions.length === 0 ? (
          <EmptyState
            icon="📝"
            title="这次听写没有题目"
            description="正常情况下不会有这种会话（布置时一个词都没取到会直接报错）。请重新布置一次。"
            action={
              <Link to={`/students/${id}/dictation`} className="mt-2">
                <Button size="sm">回听写页</Button>
              </Link>
            }
          />
        ) : (
          <>
            {/* ============================================ 播放控制（手机优先） */}
            <Card className="bg-brand-50/60">
              <div className="text-center">
                <p className="text-xs font-medium text-brand-700">当前进度</p>
                <p className="mt-1 text-3xl font-bold text-slate-900">
                  {snapshot.position === null ? '—' : `第 ${snapshot.position} 题`}
                  <span className="ml-2 text-base font-medium text-slate-500">
                    / 共 {questions.length} 题
                  </span>
                </p>
                {/*
                  ⚠️ 声调符号是**给孩子看的**（家长读到音不对时可以指出"是 zhǎng 不是 cháng"）。
                      它**不会**被念出来 —— TTS 读不了拼音，念的永远是词本身。
                */}
                {currentQuestion !== null && (
                  <p className="mt-1 font-mono text-sm text-slate-500">
                    {currentQuestion.pinyin === '' ? '（没有读音）' : currentQuestion.pinyin}
                  </p>
                )}
                <p className="mt-2 text-sm text-slate-600">
                  {snapshot.finished
                    ? '这一轮读完了 ✅'
                    : !snapshot.gesture
                      ? '准备好之后，按下面这个按钮开始 —— 点了才会出声。'
                      : snapshot.waiting
                        ? `等 ${remainingSeconds === null ? '—' : remainingSeconds.toFixed(1)} 秒`
                        : snapshot.repeat === null
                          ? '正在读…'
                          : `正在读第 ${snapshot.repeat} 遍`}
                </p>

                {/* 总进度条 */}
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-[width] duration-300"
                    style={{ width: `${Math.round(snapshot.progress * 100)}%` }}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(snapshot.progress * 100)}
                    aria-label="听写进度"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  已进行 {formatClock(snapshot.elapsedMs / 1000)} / 约{' '}
                  {formatClock(snapshot.totalMs / 1000)}
                </p>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {!snapshot.gesture || snapshot.finished ? (
                  <Button size="lg" icon={<IconPlay size={20} />} onClick={onStartPressed}>
                    {snapshot.finished ? '从头再放一遍' : '开始播放'}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    variant={snapshot.playing ? 'secondary' : 'primary'}
                    icon={snapshot.playing ? <IconPause size={20} /> : <IconPlay size={20} />}
                    onClick={() => player.toggle()}
                  >
                    {snapshot.playing ? '暂停' : '继续'}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  icon={<IconReplay size={18} />}
                  disabled={!snapshot.gesture || snapshot.position === null}
                  onClick={() => player.replayCurrent()}
                >
                  重播这一题
                </Button>
                <Button
                  variant="secondary"
                  icon={<IconSkipForward size={18} />}
                  disabled={!snapshot.gesture || snapshot.position === null}
                  onClick={() => player.skipCurrent()}
                >
                  跳过这一题
                </Button>
                {snapshot.gesture && !snapshot.finished && (
                  <Button variant="ghost" onClick={() => player.stop()}>
                    结束这一轮
                  </Button>
                )}
              </div>

              {/* 出声能力的三条说明（**不是**报错，但都必须说出来） */}
              {!player.speechSupported && (
                <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <span className="mt-0.5 shrink-0">
                    <IconAlertTriangle size={16} />
                  </span>
                  <span>
                    这台设备/浏览器不支持语音朗读。有家长录音的词还能放出来，
                    其它的需要换一台设备，或者先把这些词录一遍。
                  </span>
                </p>
              )}
              {player.audioLoading && (
                <p className="mt-3 text-center text-xs text-slate-500">正在取家长录音…</p>
              )}
              {player.audioError !== null && (
                <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <span className="mt-0.5 shrink-0">
                    <IconAlertTriangle size={16} />
                  </span>
                  <span>{player.audioError}</span>
                </p>
              )}
              {finishError !== null && (
                <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  读完了，但没能把这个会话标记成「已完成」：{finishError}
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ml-2"
                    loading={finishMutation.isPending}
                    onClick={() => finishMutation.mutate()}
                  >
                    再试一次
                  </Button>
                </p>
              )}
            </Card>

            {/* ============================================ 题目网格（手点跳题） */}
            <Card>
              <CardHeader
                title="全部题目"
                description="想跳到哪一题就点哪一题。已经开始听的那些会标出来。"
                actionAlign="start"
                action={
                  <div className="flex basis-full flex-wrap items-center gap-2">
                    <Badge tone="neutral">
                      家长录音 {questions.filter((q) => q.spokenSource === 'parent_audio').length} 个
                    </Badge>
                    <Badge tone="neutral">
                      浏览器语音 {questions.filter((q) => q.spokenSource === 'tts').length} 个
                    </Badge>
                    <Link to={`/students/${id}/dictation?recording=1`}>
                      <Button size="sm" variant="secondary" icon={<IconMic size={16} />}>
                        去录音
                      </Button>
                    </Link>
                  </div>
                }
              />
              <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-8">
                {questions.map((question) => {
                  const isCurrent = snapshot.position === question.position;
                  /**
                   * "已经读过"的判据：题号在当前位置**之前**。
                   * ⚠️ 不能用 `stepIndex > 0` 单独做判据 —— 时间线上的第 0 格就是第 1 题的
                   *    第一遍，所以刚开始播第 1 题时 `stepIndex` 仍是 0，这正好是对的。
                   */
                  const played =
                    snapshot.position !== null && question.position < snapshot.position;
                  return (
                    <li key={question.id} className="min-w-0">
                      <button
                        type="button"
                        onClick={() => player.seek(question.position)}
                        className={
                          isCurrent
                            ? 'w-full rounded-xl border-2 border-brand-500 bg-brand-50 px-2 py-2 text-center'
                            : 'w-full rounded-xl border border-slate-200 px-2 py-2 text-center hover:bg-slate-50'
                        }
                      >
                        <span className="block text-[11px] text-slate-400">
                          {question.position}
                          {played ? ' · 已听' : ''}
                        </span>
                        <span className="block truncate text-sm font-semibold text-slate-900">
                          {question.wordText}
                        </span>
                        <span className="mt-0.5 flex items-center justify-center gap-0.5 text-[10px] text-slate-400">
                          {question.spokenSource === 'parent_audio' ? (
                            <>
                              <IconMic size={10} />
                              录音
                            </>
                          ) : (
                            <>
                              <IconVolume2 size={10} />
                              语音
                            </>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>

            {/* ---------- 第四步的位置（明确说清楚，不让家长找批改按钮） ---------- */}
            <Card className="bg-slate-50">
              <p className="text-sm text-slate-600">
                批改（对 / 错 / 没写出来）与错词本在**下一步**做。这一步只负责把词按节奏读出来，
                所以这里没有批改按钮 —— 现在可以对着孩子的纸自己检查。
              </p>
              {/*
                批量任务的一批：**这一页读完不会把它算成"听过了"**
                （见上面 onFinish 的注释）。家长必须知道这条，否则会以为
                "我陪他读完了，怎么进度还是没动"。
              */}
              {session.assignmentId !== null && (
                <p className="mt-2 text-sm text-slate-600">
                  这是批量任务的一批：在这里**重播不会改变任务进度** ——
                  这一批要孩子在自己的「听写」里点<strong>「提交这一批」</strong>才算听过。
                </p>
              )}
            </Card>
          </>
        )}

        <div className="flex flex-wrap gap-2">
          {/*
            2026-10-29：从播放器回列表时直接落到**「听写历史」页签** ——
            家长多半是从历史点进来听这一批的，回默认的「批量任务」页签
            等于没回原处（与批改页那个「回听写历史」同一条理由）。
          */}
          <Link to={`/students/${id}/dictation?tab=history`}>
            <Button variant="secondary" size="sm">
              回听写历史
            </Button>
          </Link>
          <Link to={`/students/${id}/dictation/${sessionId}/review`}>
            <Button variant="secondary" size="sm">
              看这次的全部题目
            </Button>
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
